import Moment from 'moment';

/**
 * Calculate the date range from a data array
 * @param {Array} data - Array of data objects
 * @param {string} timeField - The field name containing the time value
 * @returns {{ startDate: Moment, endDate: Moment, spanDays: number } | null}
 */
export const calculateDataDateRange = (data, timeField = 'timeInterval') => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const dates = data
    .map(item => {
      const dateValue = item[timeField];
      return dateValue ? Moment(dateValue) : null;
    })
    .filter(d => d && d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (dates.length === 0) {
    return null;
  }

  const startDate = dates[0];
  const endDate = dates[dates.length - 1];
  const spanDays = endDate.diff(startDate, 'days');

  return { startDate, endDate, spanDays };
};

/**
 * Generate Time Range options based on data span
 * @param {number} dataSpanDays - Number of days the data spans
 * @returns {Array<{ display: string, value: string }>}
 */
export const generateTimeRangeOptions = (dataSpanDays) => {
  const options = [];

  // Always add "All Data" as the first option
  options.push({ display: "All Data", value: "all" });
  // Always allow a bounded custom range
  // options.push({ display: "Custom", value: "custom" });

  if (dataSpanDays >= 365) { // 1+ year
    options.push(
      { display: "Year till date (YTD)", value: "ytd" },
      { display: "Last 1 year", value: "1y" },
      { display: "Last 6 months", value: "6m" },
      { display: "Last 3 months", value: "3m" },
      { display: "Last 1 month", value: "1m" }
    );
  } else if (dataSpanDays >= 180) { // 6+ months
    options.push(
      { display: "Last 3 months", value: "3m" },
      { display: "Last 1 month", value: "1m" },
      { display: "Last 2 weeks", value: "2w" }
    );
  } else if (dataSpanDays >= 90) { // 3+ months
    options.push(
      { display: "Last 1 month", value: "1m" },
      { display: "Last 2 weeks", value: "2w" },
      { display: "Last 1 week", value: "1w" }
    );
  } else if (dataSpanDays >= 30) { // 1+ month
    options.push(
      { display: "Last 2 weeks", value: "2w" },
      { display: "Last 1 week", value: "1w" }
    );
  } else if (dataSpanDays >= 7) { // 1+ week
    options.push(
      { display: "Last 1 week", value: "1w" },
      { display: "Last 48 hours", value: "48h" }
    );
  } else if (dataSpanDays >= 2) { // 2+ days
    options.push(
      { display: "Last 48 hours", value: "48h" },
      { display: "Last 24 hours", value: "24h" }
    );
  }

  return options;
};

/**
 * Time Range value to days mapping
 */
const TIME_RANGE_TO_DAYS = {
  'all': Infinity,
  'ytd': 365,
  '1y': 365,
  '6m': 180,
  '3m': 90,
  '1m': 30,
  '2w': 14,
  '1w': 7,
  '48h': 2,
  '24h': 1,
};

/**
 * Get valid Time Interval options for a selected Time Range
 * @param {string} selectedTimeRange - The selected time range value (e.g., "3m", "1m", "all")
 * @param {number} dataSpanDays - The actual data span in days (used for "all" option)
 * @returns {Array<{ display: string, value: string }>}
 */
export const getIntervalOptionsForTimeRange = (selectedTimeRange, dataSpanDays = 365) => {
  const rangeDays = (selectedTimeRange === 'all' || selectedTimeRange === 'custom')
    ? dataSpanDays
    : TIME_RANGE_TO_DAYS[selectedTimeRange] || 30;

  // Define interval options based on range
  if (rangeDays <= 1) { // 24 hours or less
    return [
      { display: "Hour", value: "hour" }
    ];
  } else if (rangeDays <= 2) { // 48 hours
    return [
      { display: "Hour", value: "hour" }
    ];
  } else if (rangeDays <= 7) { // 1 week
    return [
      { display: "Day", value: "day" }
    ];
  } else if (rangeDays <= 14) { // 2 weeks
    return [
      { display: "Day", value: "day" },
      { display: "Week", value: "week" }
    ];
  } else if (rangeDays <= 30) { // 1 month
    return [
      { display: "Day", value: "day" },
      { display: "Week", value: "week" }
    ];
  } else if (rangeDays <= 90) { // 3 months
    return [
      { display: "Week", value: "week" },
      { display: "Month", value: "month" }
    ];
  } else if (rangeDays <= 180) { // 6 months
    return [
      { display: "Week", value: "week" },
      { display: "Month", value: "month" }
    ];
  } else { // 6+ months
    return [
      { display: "Month", value: "month" },
      { display: "Year", value: "year" }
    ];
  }
};

/**
 * Filter data by selected time range
 * @param {Array} data - Array of data objects
 * @param {string} selectedRange - The selected time range value (e.g., "3m", "1m", "all")
 * @param {string} timeField - The field name containing the time value
 * @returns {Array} - Filtered data array
 */
export const filterDataByTimeRange = (data, selectedRange, timeField = 'timeInterval') => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return data;
  }

  if (selectedRange === 'all') {
    return data;
  }

  // Calculate cutoff date (from the most recent data point)
  const dates = data
    .map(item => item[timeField] ? Moment(item[timeField]) : null)
    .filter(d => d && d.isValid());

  if (dates.length === 0) {
    return data;
  }

  const maxDate = Moment.max(dates);
  let cutoffDate;
  if (selectedRange === 'ytd') {
    cutoffDate = maxDate.clone().startOf('year');
  } else {
    const rangeDays = TIME_RANGE_TO_DAYS[selectedRange];
    if (!rangeDays) {
      return data;
    }
    cutoffDate = maxDate.clone().subtract(rangeDays, 'days');
  }

  return data.filter(item => {
    const itemDate = item[timeField] ? Moment(item[timeField]) : null;
    return itemDate && itemDate.isValid() && itemDate.isSameOrAfter(cutoffDate);
  });
};

/**
 * Filter data by a custom date range (inclusive).
 * @param {Array} data - Array of data objects
 * @param {{ start: string | null, end: string | null }} range - ISO date strings (or nulls)
 * @param {string} timeField - The field name containing the time value
 * @returns {Array} - Filtered data array
 */
export const filterDataByCustomRange = (data, range, timeField = 'timeInterval') => {
  if (!data || !Array.isArray(data) || data.length === 0) return data;
  if (!range || !range.start || !range.end) return data;

  const start = Moment(range.start);
  const end = Moment(range.end);
  if (!start.isValid() || !end.isValid()) return data;

  const startBound = Moment.min(start, end);
  const endBound = Moment.max(start, end);

  return data.filter(item => {
    const itemDate = item[timeField] ? Moment(item[timeField]) : null;
    return (
      itemDate &&
      itemDate.isValid() &&
      itemDate.isSameOrAfter(startBound, 'day') &&
      itemDate.isSameOrBefore(endBound, 'day')
    );
  });
};

/**
 * Get default interval for a time range
 * @param {string} selectedTimeRange - The selected time range value
 * @param {number} dataSpanDays - The actual data span in days
 * @returns {{ display: string, value: string }}
 */
export const getDefaultIntervalForTimeRange = (selectedTimeRange, dataSpanDays = 365) => {
  const options = getIntervalOptionsForTimeRange(selectedTimeRange, dataSpanDays);
  return options[0] || { display: "Day", value: "day" };
};
